/**
 * Etapa 1: Upload e parse do arquivo CSV
 * 
 * Permite ao usuário escolher entre modelo padrão ou WhatsApp Oficial
 * (com colunas PIX e URLs dinâmicas para templates)
 * Também permite selecionar o DDI padrão para normalização automática de telefones
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow } from
'@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from
'@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/components/ui/select';
import {
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  MessageSquare,
  BadgeCheck,
  Globe } from
'lucide-react';

import {
  parseCSV,
  CSVParseResult,
  downloadCSVTemplate,
  downloadCSVTemplateOficial,
  formatFileSize,
  REQUIRED_COLUMNS,
  extractVariableColumns } from
'@/lib/csv-utils';
import { countryCodes } from '@/lib/country-codes';

// =====================================================
// TIPOS
// =====================================================

interface CSVUploadStepProps {
  onComplete: (data: CSVParseResult, defaultCountryCode: string) => void;
}

// =====================================================
// COMPONENTE
// =====================================================

export default function CSVUploadStep({ onComplete }: CSVUploadStepProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<CSVParseResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Estado para o DDI padrão (Brasil como default)
  const [defaultCountryCode, setDefaultCountryCode] = useState('55');

  // Handler de arquivo
  const handleFile = useCallback(async (selectedFile: File) => {
    setError(null);

    // Validar extensão
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Por favor, selecione um arquivo CSV');
      return;
    }

    // Validar tamanho (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('Arquivo muito grande. Máximo permitido: 10MB');
      return;
    }

    setFile(selectedFile);

    // Ler e parsear arquivo
    try {
      const content = await selectedFile.text();
      const result = parseCSV(content);

      if (result.errors.length > 0) {
        setError(result.errors.join('. '));
        return;
      }

      if (result.totalRows === 0) {
        setError('Arquivo CSV vazio ou sem dados');
        return;
      }

      if (result.totalRows > 10000) {
        setError('Limite máximo de 10.000 contatos por importação');
        return;
      }

      setParseResult(result);
    } catch (err) {
      setError('Erro ao ler arquivo. Verifique se é um CSV válido.');
    }
  }, []);

  // Handlers de drag & drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  };

  // Reset
  const handleReset = () => {
    setFile(null);
    setParseResult(null);
    setError(null);
  };

  // Avançar
  const handleContinue = () => {
    if (parseResult) {
      onComplete(parseResult, defaultCountryCode);
    }
  };

  // Obter informações do país selecionado
  const selectedCountry = countryCodes.find((c) => c.code.replace('+', '') === defaultCountryCode);

  // =====================================================
  // RENDER
  // =====================================================

  const variableColumns = parseResult ? extractVariableColumns(parseResult.headers) : [];

  return (
    <div
      className="space-y-6 w-full"
      style={{
        maxWidth: '100%',
        overflow: 'hidden',
        contain: 'inline-size'
      }}>

      {/* Instrução e download do modelo */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Selecione o arquivo CSV</h3>
          <p className="text-sm text-muted-foreground">
            O arquivo deve conter a coluna "telefone" obrigatoriamente
          </p>
        </div>
        
        {/* Dropdown para escolher tipo de modelo */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Baixar Modelo
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuItem onClick={downloadCSVTemplate}>
              <MessageSquare className="h-4 w-4 mr-2 text-green-500" />
              <div className="flex flex-col">
                <span>Modelo Padrão</span>
                <span className="text-xs text-muted-foreground">
                  WhatsApp via QR Code
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={downloadCSVTemplateOficial}>
              <BadgeCheck className="h-4 w-4 mr-2 text-blue-500" />
              <div className="flex flex-col">
                <span>Modelo WhatsApp Oficial</span>
                <span className="text-xs text-muted-foreground">
                  Inclui colunas PIX e URLs dinâmicas
                </span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Zona de upload */}
      {!parseResult ?
      <Card
        className={`border-2 border-dashed transition-colors ${
        isDragging ?
        'border-primary bg-primary/5' :
        'border-muted-foreground/25 hover:border-primary/50'}`
        }
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>

          <CardContent className="py-12">
            <div className="text-center">
              <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">
                Arraste o arquivo CSV aqui
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                ou clique para selecionar
              </p>
              
              <label>
                <input
                type="file"
                accept=".csv"
                onChange={handleInputChange}
                className="hidden" />

                <Button variant="secondary" asChild>
                  <span>Selecionar Arquivo</span>
                </Button>
              </label>
              
              <p className="text-xs text-muted-foreground mt-4">
                Máximo: 1.000 contatos • Até 10MB
              </p>
            </div>
          </CardContent>
        </Card> :

      <>
          {/* Arquivo selecionado */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-8 w-8 text-primary" />
                  <div>
                    <p className="font-medium">{file?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {file && formatFileSize(file.size)} • {parseResult.totalRows} linhas
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={handleReset}>
                  Trocar arquivo
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Colunas detectadas - com scroll horizontal para muitas colunas */}
          <div
          className="space-y-2"
          style={{
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>

            <h4 className="font-medium">Colunas detectadas:</h4>
            
            {/* Container com scroll horizontal */}
            <div
            className="relative"
            style={{ overflow: 'hidden', width: '100%', maxWidth: '100%' }}>

              {/* Gradiente de fade à direita indicando mais conteúdo */}
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
              
              <div
              className="flex items-center gap-2 pb-2 pr-8 overflow-x-auto overflow-y-hidden scrollbar-thin"
              style={{
                WebkitOverflowScrolling: 'touch',
                scrollBehavior: 'smooth'
              }}>

                {parseResult.headers.map((header) =>
              <Badge
                key={header}
                variant={REQUIRED_COLUMNS.includes(header) ? 'default' : 'outline'}
                className="shrink-0">

                    {header}
                    {REQUIRED_COLUMNS.includes(header) &&
                <CheckCircle2 className="h-3 w-3 ml-1" />
                }
                  </Badge>
              )}
              </div>
            </div>
            
            {variableColumns.length > 0 &&
          <div
            className="relative"
            style={{ overflow: 'hidden', width: '100%', maxWidth: '100%' }}>

                {/* Gradiente de fade à direita */}
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
                
                <p
              className="text-sm text-muted-foreground overflow-x-auto whitespace-nowrap pb-1 pr-8 scrollbar-thin"
              style={{ WebkitOverflowScrolling: 'touch' }}>

                  Variáveis para mensagens: {variableColumns.join(', ')}
                </p>
              </div>
          }
          </div>
          
          {/* Preview dos primeiros registros - containment agressivo */}
          <div
          className="space-y-2"
          style={{
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            contain: 'inline-size'
          }}>

            <h4 className="font-medium">Preview (primeiros 10 registros):</h4>
            
            {/* Container com scroll horizontal TOTALMENTE contido */}
            <div
            className="relative"
            style={{
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>

              {/* Gradiente de fade à direita indicando mais conteúdo */}
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
              
              <div
              className="border rounded-md overflow-auto scrollbar-thin"
              style={{
                width: '100%',
                maxWidth: '100%',
                WebkitOverflowScrolling: 'touch'
              }}>

                {/* Wrapper interno com largura mínima baseada nas colunas */}
                <div style={{ minWidth: `${Math.max(parseResult.headers.length * 150, 400)}px` }}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12 whitespace-nowrap">#</TableHead>
                        {parseResult.headers.map((header) =>
                      <TableHead key={header} className="whitespace-nowrap min-w-[120px]">
                            {header}
                          </TableHead>
                      )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parseResult.rows.slice(0, 10).map((row, index) =>
                    <TableRow key={index}>
                          <TableCell className="text-muted-foreground whitespace-nowrap">{index + 1}</TableCell>
                          {parseResult.headers.map((header) =>
                      <TableCell key={header} className="max-w-[200px] truncate whitespace-nowrap">
                              {row[header] || '-'}
                            </TableCell>
                      )}
                        </TableRow>
                    )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
            
            {parseResult.totalRows > 10 &&
          <p className="text-sm text-muted-foreground text-center">
                ... e mais {parseResult.totalRows - 10} registros
              </p>
          }
          </div>
          
          {/* Botão de continuar */}
          <div className="flex justify-end">
            <Button onClick={handleContinue}>
              Continuar
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </>
      }
      
      {/* Mensagem de erro */}
      {error &&
      <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      }
      
      {/* Seletor de DDI padrão */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-medium text-sm">Código do País (DDI)</h4>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Será adicionado automaticamente nos telefones que não tiverem DDI
            </p>
          </div>
          <Select value={defaultCountryCode} onValueChange={setDefaultCountryCode}>
            <SelectTrigger className="w-[180px]">
              <SelectValue>
                {selectedCountry ? `${selectedCountry.flag} ${selectedCountry.country} (${selectedCountry.code})` : 'Selecione...'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {countryCodes.slice(0, 10).map((country) =>
              <SelectItem key={country.code} value={country.code.replace('+', '')}>
                  {country.flag} {country.country} ({country.code})
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Dicas */}
      <div className="bg-muted/50 rounded-lg p-4 space-y-2">
        <h4 className="font-medium text-sm">Formato do telefone:</h4>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• Formato recomendado: <code className="bg-muted px-1 rounded">DDI + DDD + número</code></li>
          <li>• Exemplo: <code className="bg-muted px-1 rounded">5562999998888</code></li>
          <li>• <strong>Sem DDI?</strong> O sistema adiciona automaticamente (+{defaultCountryCode})</li>
          <li>• Celular com 9 dígitos ou fixo com 8 dígitos</li>
        </ul>
      </div>
    </div>);

}